import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSections = [
    {
      title: "Data Source & Trust",
      questions: [
        {
          question: "Where does the data come from?",
          answer: "All data processed by SHOWME comes directly from official Missouri Ethics Commission (MEC) campaign finance PDF reports uploaded by users. SHOWME does not generate, estimate, or source campaign finance data independently."
        },
        {
          question: "Is SHOWME affiliated with the Missouri Ethics Commission?",
          answer: "No. SHOWME is an independent data preparation and analysis tool. It is not affiliated with or endorsed by the Missouri Ethics Commission."
        },
        {
          question: "Is the data accurate?",
          answer: "SHOWME extracts and presents data as it appears in the uploaded filings. While every effort is made to extract information accurately, the quality of the output depends on the completeness and clarity of the original reports. Users should always refer to the original MEC filings for official totals, legal verification, or compliance purposes."
        }
      ]
    },
    {
      title: "What SHOWME Does",
      questions: [
        {
          question: "What does SHOWME actually do?",
          answer: "SHOWME converts campaign finance PDF filings into clean, structured spreadsheets and data views. This allows users to analyze donations and expenses without manually copying data from PDFs."
        },
        {
          question: "Does SHOWME change or reinterpret the data?",
          answer: "No. SHOWME does not alter reported figures, estimate missing values, or apply editorial or political judgment. All outputs reflect the data reported in the original filings."
        },
        {
          question: "What file formats do I get?",
          answer: "Users can download extracted data as CSV or Excel files. These formats are designed for use in spreadsheets, databases, and analytical tools."
        }
      ]
    },
    {
      title: "Amendments, Duplicates, and Filing Rules",
      questions: [
        {
          question: "How does SHOWME handle amended reports?",
          answer: "Missouri allows committees to file amended versions of previously submitted reports. When multiple filings exist for the same committee and reporting period, SHOWME treats the most recently filed report as authoritative. If a newer filing supersedes an older one, previously extracted data from the older report is replaced.\n\nIf an older report is uploaded after a newer filing already exists, it is recognized as superseded and does not overwrite current data."
        },
        {
          question: "What happens if I upload the same report more than once?",
          answer: "Duplicate uploads are automatically detected. When you upload a report that already exists in the system, you get instant access without it counting against your annual upload quota."
        },
        {
          question: "Why might totals change after uploading a new report?",
          answer: "Totals may change when an amended filing replaces a previously submitted report for the same reporting period. This reflects how amended filings are intended to function under MEC rules."
        }
      ]
    },
    {
      title: "Analysis & Neutrality",
      questions: [
        {
          question: "Does SHOWME provide political analysis or opinions?",
          answer: "No. SHOWME provides neutral, data-derived summaries and analytical views based solely on reported figures. It does not assess intent, evaluate compliance, or apply political or legal interpretation."
        },
        {
          question: "What kind of analysis does SHOWME provide?",
          answer: "SHOWME provides rule-based, deterministic analysis built directly on the extracted data. Examples include:\n\n• Aggregated totals across reports\n• Time-based summaries of contributions and expenses\n• Donor and payee frequency counts\n• Transaction groupings and rollups\n\nThese views help users explore and understand the data more efficiently without introducing new facts or subjective interpretation."
        }
      ]
    },
    {
      title: "Use, Scope, and Responsibility",
      questions: [
        {
          question: "Can I rely on SHOWME for official or legal purposes?",
          answer: "SHOWME is designed to support analysis, research, and review. For official totals, legal filings, or compliance determinations, users should always consult the original Missouri Ethics Commission reports."
        },
        {
          question: "What should I do if something looks incorrect?",
          answer: "If an extracted value appears unclear or unexpected, users should review the original filing. Support is available for questions about platform behavior or data handling, but SHOWME does not modify reported figures."
        },
        {
          question: "Who typically uses SHOWME?",
          answer: "SHOWME is used by journalists, researchers, campaigns, consultants, advocacy groups, civic organizations, and members of the public who need to work with Missouri campaign finance data efficiently."
        }
      ]
    },
    {
      title: "Support",
      questions: [
        {
          question: "How can I contact support?",
          answer: "Questions, issues, or feedback are welcome. You can contact support through the platform or by using the contact information provided on the site."
        }
      ]
    }
  ];

  let questionIndex = 0;

  return (
    <div style={{
      maxWidth: "860px",
      margin: "0 auto",
      padding: "4rem 2rem"
    }}>
      {/* Page Title */}
      <h1 style={{
        fontSize: "2.25rem",
        fontWeight: "800",
        color: "#111827",
        marginBottom: "3rem",
        lineHeight: "1.2"
      }}>
        Frequently Asked Questions
      </h1>

      {faqSections.map((section, sectionIdx) => (
        <div key={sectionIdx}>
          {/* Section Header */}
          <h2 style={{
            fontSize: "1.5rem",
            fontWeight: "700",
            color: "#111827",
            marginTop: sectionIdx === 0 ? "0" : "4rem",
            marginBottom: "1.5rem"
          }}>
            {section.title}
          </h2>

          {/* Questions in this section */}
          {section.questions.map((item) => {
            const currentIndex = questionIndex++;
            const isOpen = openIndex === currentIndex;

            return (
              <div
                key={currentIndex}
                style={{
                  marginBottom: "1rem",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  overflow: "hidden"
                }}
              >
                {/* Question Header */}
                <button
                  onClick={() => toggleQuestion(currentIndex)}
                  style={{
                    width: "100%",
                    padding: "1rem 1.25rem",
                    background: isOpen ? "#f9fafb" : "white",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    textAlign: "left",
                    transition: "background-color 0.2s"
                  }}
                  onMouseEnter={(e) => {
                    if (!isOpen) e.currentTarget.style.backgroundColor = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    if (!isOpen) e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  <strong style={{
                    fontSize: "1.05rem",
                    color: "#111827",
                    fontWeight: "600",
                    lineHeight: "1.5"
                  }}>
                    {item.question}
                  </strong>
                  {isOpen ? (
                    <ChevronUp size={20} style={{ color: "#6b7280", flexShrink: 0, marginLeft: "1rem" }} />
                  ) : (
                    <ChevronDown size={20} style={{ color: "#6b7280", flexShrink: 0, marginLeft: "1rem" }} />
                  )}
                </button>

                {/* Answer */}
                {isOpen && (
                  <div style={{
                    padding: "1rem 1.25rem 1.5rem",
                    background: "white",
                    borderTop: "1px solid #e5e7eb"
                  }}>
                    {item.answer.split('\n').map((paragraph, idx) => (
                      <p
                        key={idx}
                        style={{
                          color: "#4b5563",
                          fontSize: "1rem",
                          lineHeight: "1.7",
                          margin: idx === 0 ? "0" : "1rem 0 0 0"
                        }}
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Contact Support Section */}
      <section style={{
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "10px",
        padding: "2rem",
        textAlign: "center",
        marginTop: "4rem"
      }}>
        <h2 style={{
          fontSize: "1.5rem",
          fontWeight: "700",
          color: "#111827",
          marginBottom: "1rem"
        }}>
          Still Have Questions?
        </h2>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.7",
          marginBottom: "1.5rem",
          fontSize: "1.05rem"
        }}>
          Contact support for additional help.
        </p>
        <a
          href="mailto:support@showmecampaignfinances.com"
          style={{
            display: "inline-block",
            padding: "0.75rem 1.5rem",
            background: "linear-gradient(90deg, #2563eb, #4f46e5)",
            color: "white",
            borderRadius: "8px",
            fontWeight: "600",
            textDecoration: "none",
            boxShadow: "0 2px 8px rgba(37, 99, 235, 0.2)",
            transition: "opacity 0.2s"
          }}
          onMouseEnter={(e) => e.target.style.opacity = "0.9"}
          onMouseLeave={(e) => e.target.style.opacity = "1"}
        >
          Contact Support
        </a>
      </section>
    </div>
  );
};
